import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { MARKETPLACE_CATEGORIES } from '@/lib/marketplaceNavigation';
import { BOOKSTERA_READER_APP_URL } from '@/lib/readerApp';
import { cn } from '@/lib/utils';
import { ClipboardCheck, Search, Smartphone, Users, X, BookOpenCheck, Sparkles } from 'lucide-react';
import { FormEvent, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import useLocalStorage from 'use-local-storage';
import { DesktopNav } from './navbar/DesktopNav';
import { MobileNav } from './navbar/MobileNav';
import { UserMenu } from './navbar/UserMenu';

const TOP_BANNER_ITEMS = [
  {
    eyebrow: 'Bookstera Black Month',
    title: '30% off all annual subscription plans',
    cta: 'Claim offer',
    ctaMobile: '30% off',
    href: '/pricing?blackmonth=1',
    Icon: Sparkles,
    tone: 'from-black via-slate-950 to-zinc-800',
  },
  {
    eyebrow: 'Join the community',
    title: 'Join the Bookstera self-publishing Facebook group',
    cta: 'Join the Facebook group',
    ctaMobile: 'Join group',
    href: 'https://www.facebook.com/groups/selfpublishingforentrepreneurs',
    Icon: Users,
    tone: 'from-blue-950 via-blue-700 to-indigo-600',
  },
  {
    eyebrow: 'New on Android',
    title: 'The Bookstera Reader App is here',
    cta: 'Get it on Google Play',
    ctaMobile: 'Get app',
    href: BOOKSTERA_READER_APP_URL,
    Icon: Smartphone,
    tone: 'from-emerald-950 via-emerald-700 to-lime-600',
  },
  {
    eyebrow: 'Free resource',
    title: 'Get the free 37-point self-publishing checklist',
    cta: 'Get the checklist',
    ctaMobile: 'Get checklist',
    href: 'https://www.bookstera.com/landing/57711db2-5807-4c66-a967-da95e8573340',
    image: 'https://arzdpniijwnzlpkukabn.supabase.co/storage/v1/object/public/book-covers/book-covers/57711db2-5807-4c66-a967-da95e8573340-cover-1789383983049.png',
    Icon: ClipboardCheck,
    tone: 'from-violet-950 via-violet-700 to-fuchsia-600',
  },
] as const;

export function Navbar() {
  const navigationRow = useRef<HTMLDivElement>(null);
  const desktopContent = useRef<HTMLDivElement>(null);
  const accountActions = useRef<HTMLDivElement>(null);
  const [desktopFits, setDesktopFits] = useState(false);
  const [user, setUser] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [marketplaceSearch, setMarketplaceSearch] = useState('');
  const [activeMarketplaceCategory, setActiveMarketplaceCategory] =
    useState('All');
  const [hasDismissedReaderAppBanner, setHasDismissedReaderAppBanner] = useLocalStorage(
    'reader-app-banner-dismissed',
    false
  );
  const [topBannerIndex, setTopBannerIndex] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
const { toast } = useToast();
  const planType = location.state?.planType;
  const applyDiscount = location.state?.applyDiscount;
  const isLifetimeDeal = location.state?.isLifetimeDeal;
  const useCurrency = location.state?.useCurrency;
  const useBillingCycle = location.state?.useBillingCycle;
  const from = location.state?.from || '/';
  const isMarketplaceActive =
    location.pathname === '/marketplace' ||
    location.pathname.startsWith('/marketplace/');
  const showMarketplaceControls =
    location.pathname === '/marketplace';

  useEffect(() => {
    const fetchUserAndNavigate = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (user) {
        if (planType) {
          navigate('/pricing', {
            state: { selectedPlan: planType, initiateCheckout: true, applyDiscount, useCurrency, useBillingCycle },
            replace: true,
          });
        } else if (isLifetimeDeal) {
          navigate('/lifetime-checkout', {
            state: { initiateCheckout: true },
            replace: true,
          });
        }
      }
    };

    fetchUserAndNavigate();
  }, [planType, isLifetimeDeal, navigate]);

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      setUser(user);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);

      if (event === 'PASSWORD_RECOVERY') {
        console.log('Redirecting to password recovery');
        // Redirect to password reset page for password recovery
        navigate('/profile#account', { replace: true });
      }

      if (event === 'SIGNED_IN') {
        const planType = location.state?.planType;
        const applyDiscount = location.state?.applyDiscount;
        const useCurrency = location.state?.useCurrency;
        const useBillingCycle = location.state?.useBillingCycle;
        const isLifetimeDeal = location.state?.isLifetimeDeal;
        if (planType) {
          navigate('/pricing', {
            state: { selectedPlan: planType, initiateCheckout: true, applyDiscount, useCurrency, useBillingCycle },
            replace: true,
          });
        } else if (isLifetimeDeal) {
          navigate('/lifetime-checkout', {
            state: { initiateCheckout: true },
            replace: true,
          });
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [from, isLifetimeDeal, navigate, planType, toast]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (hasDismissedReaderAppBanner) return;
    const timer = window.setInterval(() => {
      setTopBannerIndex((index) => (index + 1) % TOP_BANNER_ITEMS.length);
    }, 7000);
    return () => window.clearInterval(timer);
  }, [hasDismissedReaderAppBanner]);

  useEffect(() => {
    if (!isMarketplaceActive) {
      setMarketplaceSearch('');
      setActiveMarketplaceCategory('All');
    }
  }, [isMarketplaceActive]);

  useEffect(() => {
    const handleCategoryChange = (event: CustomEvent) => {
      if (typeof event.detail?.category === 'string') {
        setActiveMarketplaceCategory(event.detail.category);
      }
    };

    window.addEventListener(
      'marketplace-category-changed',
      handleCategoryChange as EventListener
    );

    return () =>
      window.removeEventListener(
        'marketplace-category-changed',
        handleCategoryChange as EventListener
      );
  }, []);

  const dispatchMarketplaceSearch = (query: string) => {
    window.dispatchEvent(
      new CustomEvent('marketplace-search', {
        detail: { query },
      })
    );
  };

  const dispatchMarketplaceCategory = (category: string) => {
    window.dispatchEvent(
      new CustomEvent('marketplace-category-select', {
        detail: { category },
      })
    );
  };

  const handleMarketplaceSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const query = marketplaceSearch.trim();

    if (location.pathname !== '/marketplace') {
      navigate('/marketplace');
      window.setTimeout(() => dispatchMarketplaceSearch(query), 0);
      return;
    }

    dispatchMarketplaceSearch(query);
  };

  const handleMarketplaceCategory = (category: string) => {
    setActiveMarketplaceCategory(category);

    if (location.pathname !== '/marketplace') {
      navigate('/marketplace');
      window.setTimeout(() => dispatchMarketplaceCategory(category), 0);
      return;
    }

    dispatchMarketplaceCategory(category);
  };

  useEffect(() => {
    const row = navigationRow.current;
    const desktop = desktopContent.current;
    const actions = accountActions.current;
    if (!row || !desktop || !actions) return;
    // Keep the desktop content measurable even in compact mode. Include all
    // account and marketplace controls, plus space between the two groups.
    const measure = () => setDesktopFits(
      Math.ceil(desktop.getBoundingClientRect().width + actions.getBoundingClientRect().width) + 24 <= row.clientWidth
    );
    const observer = new ResizeObserver(measure);
    observer.observe(row);
    observer.observe(desktop);
    observer.observe(actions);
    measure();
    return () => observer.disconnect();
  }, []);

  const marketplaceNavControls = showMarketplaceControls ? (
    <div className="flex shrink-0 items-center gap-2">
      <form
        onSubmit={handleMarketplaceSearch}
        className="flex w-[180px] shrink-0 shadow-sm 2xl:w-64"
      >
        <Input
          placeholder="Search ebooks, authors, topics..."
          value={marketplaceSearch}
          onChange={(event) => setMarketplaceSearch(event.target.value)}
          className="h-9 rounded-r-none border-r-0 text-sm focus-visible:ring-primary/30"
        />
        <button
          type="submit"
          className="flex h-9 items-center justify-center rounded-r-md bg-primary px-3 transition-colors hover:bg-primary-dark"
          aria-label="Search marketplace"
        >
          <Search className="h-4 w-4 text-white" />
        </button>
      </form>

      <div className="hidden xl:block">
      <Select
        value={activeMarketplaceCategory}
        onValueChange={handleMarketplaceCategory}
      >
        <SelectTrigger className="h-9 w-40 shrink-0 rounded-full border-orange-200 bg-white px-4 text-sm font-semibold text-slate-700 shadow-sm focus:ring-secondary/30 xl:w-44">
          <SelectValue placeholder="Category" />
        </SelectTrigger>
        <SelectContent align="end" className="max-h-80">
          {MARKETPLACE_CATEGORIES.map((category) => (
            <SelectItem key={category} value={category}>
              {category}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      </div>
    </div>
  ) : null;

  return (
    <>
      {!hasDismissedReaderAppBanner && (
        <div
          className={`relative z-50 w-full max-w-full overflow-hidden border-b border-white/20 bg-gradient-to-r ${TOP_BANNER_ITEMS[topBannerIndex].tone} text-white shadow-[inset_0_-1px_0_rgba(255,255,255,0.14)]`}
        >
          <div className="relative mx-auto w-full max-w-6xl min-w-0 overflow-hidden px-2.5 py-1.5 pr-9 sm:px-4 sm:pr-12 lg:py-2">
            {(() => {
              const item = TOP_BANNER_ITEMS[topBannerIndex];
              const Icon = item.Icon;
              return [item].map((currentItem) => <a
              key={currentItem.href}
              href={item.href}
              target={item.href.startsWith('http') ? '_blank' : undefined}
              rel={item.href.startsWith('http') ? 'noopener noreferrer' : undefined}
              className="top-banner-content group flex min-w-0 max-w-full items-center justify-center gap-2 overflow-hidden rounded-md text-center sm:gap-3"
            >
              {'image' in item && <img src={item.image} alt="37-point self-publishing checklist cover" className="hidden h-8 w-6 shrink-0 rounded-sm object-cover shadow-sm transition-transform duration-300 group-hover:scale-125 sm:block" />}
              {!('image' in item) && <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white/15 text-white shadow-sm ring-1 ring-white/40 sm:h-7 sm:w-7">
                <Icon className="h-3.5 w-3.5 sm:h-4 sm:w-4" aria-hidden="true" />
              </span>}
              <span className="min-w-0 break-words text-xs font-medium leading-4 sm:text-sm sm:leading-6">
                <span className="mr-1.5 font-black uppercase tracking-[0.1em] text-[9px] text-white/75 sm:mr-2 sm:text-[10px]">{item.eyebrow}</span>
                <span className="font-bold [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2] overflow-hidden sm:[display:inline] sm:[-webkit-line-clamp:unset]">{item.title}</span>
              </span>
              <span className="shrink-0" aria-hidden="true">
                &rarr;
              </span>
              <span className="shrink-0 rounded-full bg-white px-2 py-0.5 text-[11px] font-bold text-slate-800 shadow-sm transition group-hover:bg-white/90 sm:px-3 sm:py-1 sm:text-sm">
                <span className="sm:hidden">{item.ctaMobile}</span>
                <span className="hidden sm:inline">{item.cta}</span>
              </span>
            </a>);
            })()}
            <button
              aria-label="Dismiss top announcement"
              className="absolute right-1 top-1/2 flex h-6 w-6 -translate-y-1/2 shrink-0 items-center justify-center rounded-full bg-white/15 text-white transition hover:bg-white/25 focus:outline-none focus-visible:ring-2 focus-visible:ring-white sm:right-3 sm:h-8 sm:w-8"
              onClick={() => setHasDismissedReaderAppBanner(true)}
              type="button"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
      <header
        className={cn(
          'sticky top-0 z-50 w-full border-b border-orange-200/40 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/60 transition-shadow duration-300',
          scrolled ? 'shadow-[0_1px_8px_rgba(249,115,22,0.08)]' : 'shadow-none'
        )}
      >
      <div className="mx-auto px-2.5 sm:px-4 lg:px-8">
        <div ref={navigationRow} className="relative flex h-14 items-center gap-3 sm:h-16">
          {/* Left side: Mobile nav button and logo */}
          <div className={cn("min-w-0 items-center gap-2 sm:gap-3", desktopFits ? "hidden" : "flex")}>
            <div className="shrink-0">
              <MobileNav user={user} />
            </div>
            <div className="flex min-w-0 items-center">
              <Link
                to={isMarketplaceActive ? '/marketplace' : '/'}
                className="flex items-center gap-1.5 sm:gap-2"
                aria-label={isMarketplaceActive ? 'Marketplace' : 'Bookstera'}
              >
                {isMarketplaceActive ? (
                  <span className="block h-8 w-8 overflow-hidden">
                    <img
                      src="/lovable-uploads/bookstera-logo-nobg.webp"
                      alt="Bookstera mark"
                      className="h-8 max-w-none"
                      height="32"
                      style={{ minWidth: '156px' }}
                    />
                  </span>
                ) : (
                  <img
                    src="/lovable-uploads/bookstera-logo-nobg.webp"
                    alt="Bookstera Logo"
                    className="h-auto w-[120px] max-w-full sm:w-[140px]"
                    height="20"
                    style={{ minWidth: '20px' }}
                  />
                )}
                {isMarketplaceActive && (
                  <span className="bg-gradient-to-r from-[#8b5cf6] via-[#e8793f] to-[#7c3aed] bg-clip-text text-lg font-bold tracking-tight text-transparent sm:text-xl">
                    Marketplace
                  </span>
                )}
              </Link>
            </div>
          </div>

          {/* Hidden desktop content stays measurable so resizing and account
              changes can switch modes before the links run into the actions. */}
          <div
            aria-hidden={!desktopFits}
            inert={!desktopFits ? '' : undefined}
            className={cn(desktopFits ? 'min-w-0 flex-1' : 'invisible pointer-events-none absolute inset-x-0 top-0 overflow-hidden')}
          >
            <div ref={desktopContent} className="flex w-max items-center gap-3">
              <DesktopNav user={user} />
              {marketplaceNavControls}
            </div>
          </div>

          {/* Right side: User menu */}
          <div ref={accountActions} className="ml-auto flex shrink-0 items-center justify-end gap-2 sm:gap-3">
            <div className="flex w-full items-center justify-end gap-1 sm:gap-1.5">
              <UserMenu user={user} />
            </div>
          </div>
        </div>
      </div>
      </header>
    </>
  );
}
